import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase, generateId } from '../supabase';
import * as XLSX from 'xlsx';
import {
  Users, UserPlus, Edit, Trash2, FileSpreadsheet, Download, BookOpen, Search, RefreshCw, X, CheckCircle2, AlertCircle, ArrowRightLeft, Calendar, Plus
} from 'lucide-react';
import ClassManager from './ClassManager';
import AttendanceManager from './AttendanceManager';
import LeaveManager from './LeaveManager';
import AttendanceToday from './AttendanceToday';
import StudentScheduleModal from './StudentScheduleModal';
import './StudentManager.css';

const INITIAL_FORM = {
  mahv: '', tenhv: '', sdt: '', ghichu: '',
  trangthai: 'Đang Học', imgpath: '', ngaynhaphoc: '', ngaynghihoc: '',
  diachi: '', sdtphuhuynh: '', tenphuhuynh: '', truongdanghoc: '', lopotruong: ''
};

export default function StudentManager({ activeSubTab }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');

  // Modals & Selections
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  // Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState(['']);

  // Delete Modal State
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  // Messages
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  // Transfer Class Modal State
  const [isTransferClassOpen, setIsTransferClassOpen] = useState(false);
  const [transferClassId, setTransferClassId] = useState('');

  // Scehdule Modal
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const { data: stData, error: stErr } = await supabase.from('tbl_hv').select('*').order('mahv', { ascending: true });
      if (stErr) throw stErr;

      const { data: schedData, error: schedErr } = await supabase.from('tbl_lichhoc_hv').select('mahv, malop');
      if (schedErr) throw schedErr;

      // Attach classes info to student object for easy rendering/filtering
      const studentsWithClasses = (stData || []).map(s => {
        const studentClasses = (schedData || []).filter(sc => sc.mahv === s.mahv).map(sc => sc.malop);
        return { ...s, malop_list: studentClasses };
      });
      setStudents(studentsWithClasses);

      const { data: clsData, error: clsErr } = await supabase.from('tbl_lop').select('malop, tenlop');
      if (clsErr) throw clsErr;
      setClasses(clsData || []);

      setSelectedStudentId(null);
    } catch (err) {
      console.error(err);
      showMessage('error', 'Lỗi tải dữ liệu học viên');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'students' || activeSubTab === 'classes' || activeSubTab === 'attendance_today') {
      if (students.length === 0) fetchStudents(); // Only fetch if not already loaded
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // Form Handlers
  const handleOpenAdd = async () => {
    const newMaHV = await generateId('tbl_hv', 'mahv', 'HV', 4);
    setFormData({ ...INITIAL_FORM, mahv: newMaHV });
    setSelectedClasses(['']);
    setIsEditMode(false);
    setIsFormOpen(true);
  };

  const handleOpenEdit = async () => {
    if (!selectedStudentId) return showMessage('error', 'Vui lòng chọn một học viên để sửa');
    const student = students.find(s => s.mahv === selectedStudentId);
    if (student) {
      setFormData({ ...INITIAL_FORM, ...student });
      try {
        const { data, error } = await supabase.from('tbl_lichhoc_hv').select('malop').eq('mahv', student.mahv);
        if (!error && data && data.length > 0) {
           const uniqueClasses = [...new Set(data.map(d => d.malop))];
           setSelectedClasses(uniqueClasses.length > 0 ? uniqueClasses : ['']);
        } else {
           setSelectedClasses(['']);
        }
      } catch (err) {
        setSelectedClasses(['']);
      }
      setIsEditMode(true);
      setIsFormOpen(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.mahv || !formData.tenhv) {
      return showMessage('error', 'Mã HV và Tên HV là bắt buộc');
    }

    try {
      const dataToSave = { ...formData };
      delete dataToSave.malop;      // Xóa cột cũ
      delete dataToSave.malop_list; // Xóa danh sách ảo
      delete dataToSave.tenlop;     // Xóa tên lớp ảo
      delete dataToSave.tenhv_alias;
      delete dataToSave.lichhoc;
      delete dataToSave.giohoc;

      if (isEditMode) {
        const { error } = await supabase.from('tbl_hv').update(dataToSave).eq('mahv', dataToSave.mahv);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('tbl_hv').insert([dataToSave]);
        if (error) throw error;
      }

      const validClasses = selectedClasses.filter(c => c.trim() !== '');
      if (isEditMode) {
        const { error: delError } = await supabase.from('tbl_lichhoc_hv').delete().eq('mahv', formData.mahv);
        if (delError) console.error("Lỗi xóa schedule cũ:", delError);
      }
      
      if (validClasses.length > 0) {
        // Lấy thông tin ID từ bảng tbl_lichhoc_lop tương ứng với mã lớp
        const { data: lopSchedules, error: lopErr } = await supabase.from('tbl_lichhoc_lop').select('*').in('malop', validClasses);
        if (lopErr) console.error("Lỗi lấy lịch lớp:", lopErr);
        
        let scheduleDataToInsert = [];
        for (const malop of validClasses) {
           const classScheds = (lopSchedules || []).filter(s => s.malop === malop);
           if (classScheds.length > 0) {
              classScheds.forEach(sc => {
                 scheduleDataToInsert.push({
                   mahv: dataToSave.mahv,
                   malop: malop,
                   lichhoc_lop_id: sc.id,
                   lichhoc: sc.lichhoc || '',
                   giohoc: sc.giohoc || '',
                   loailich: 'class',
                   trangthai: 'active'
                 });
              });
           } else {
              scheduleDataToInsert.push({
                mahv: dataToSave.mahv,
                malop: malop,
                loailich: 'class',
                trangthai: 'active'
              });
           }
        }

        if (scheduleDataToInsert.length > 0) {
          const { error: schedError } = await supabase.from('tbl_lichhoc_hv').insert(scheduleDataToInsert);
          if (schedError) {
             console.error("Lỗi thêm row vào tbl_lichhoc_hv:", schedError);
          }
        }
      }
      
      showMessage('success', isEditMode ? 'Cập nhật thành công' : 'Thêm học viên thành công');
      setIsFormOpen(false);
      fetchStudents();
    } catch (err) {
      console.error(err);
      if (err.code === '23505') return showMessage('error', 'Mã học viên đã tồn tại');
      showMessage('error', 'Lỗi lưu thông tin');
    }
  };

  // Delete Action
  const handleDeleteTrigger = () => {
    if (!selectedStudentId) return showMessage('error', 'Vui lòng chọn học viên để xóa');
    setDeletePassword('');
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      // Validate password against current session
      const sessionStr = localStorage.getItem('auth_session');
      if (!sessionStr) return showMessage('error', 'Phiên làm việc hết hạn');

      const session = JSON.parse(sessionStr);
      if (session.user.password !== deletePassword) {
        return showMessage('error', 'Mật khẩu xác nhận không đúng!');
      }

      const today = new Date().toISOString().split('T')[0];
      const { error } = await supabase.from('tbl_hv').update({ trangthai: 'Đã Nghỉ', ngaynghihoc: today }).eq('mahv', selectedStudentId);
      if (error) throw error;

      showMessage('success', 'Đã chuyển trạng thái học viên thành Đã Nghỉ và cập nhật ngày nghỉ');
      setIsDeleteOpen(false);
      fetchStudents();
    } catch (err) {
      console.error(err);
      showMessage('error', 'Lỗi khi xóa học viên');
    }
  };

  // Reserve/Unreserve (Bảo Lưu) Action
  const handleReserve = async () => {
    if (!selectedStudentId) return showMessage('error', 'Vui lòng chọn học viên để thao tác');

    const student = students.find(s => s.mahv === selectedStudentId);
    if (!student) return;

    const isHuyBaoLuu = student.trangthai === 'Bảo Lưu';
    const newStatus = isHuyBaoLuu ? 'Đang Học' : 'Bảo Lưu';
    const confirmMessage = isHuyBaoLuu
      ? `Bạn có chắc chắn muốn HỦY bảo lưu và chuyển học viên ${student.tenhv} sang Đang Học?`
      : `Bạn có chắc chắn muốn chuyển học viên ${student.tenhv} sang trạng thái BẢO LƯU?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const { error } = await supabase.from('tbl_hv').update({ trangthai: newStatus }).eq('mahv', selectedStudentId);
      if (error) throw error;

      showMessage('success', isHuyBaoLuu ? 'Đã hủy bảo lưu, học viên hiển thị Đang Học' : 'Đã cập nhật trạng thái Bảo Lưu');
      fetchStudents();
    } catch (err) {
      console.error(err);
      showMessage('error', 'Lỗi cập nhật trạng thái');
    }
  };

  // Transfer Class Action
  const handleTransferClassTrigger = () => {
    if (!selectedStudentId) return showMessage('error', 'Vui lòng chọn học viên cần chuyển lớp');
    setTransferClassId('');
    setIsTransferClassOpen(true);
  };

  const confirmTransferClass = async () => {
    if (!transferClassId) return showMessage('error', 'Vui lòng chọn lớp mới cần chuyển đến!');

    try {
      // 1. Delete old schedules
      const { error: delError } = await supabase.from('tbl_lichhoc_hv').delete().eq('mahv', selectedStudentId);
      if (delError) throw delError;

      // 2. Fetch new class schedule details from tbl_lichhoc_lop
      const { data: lopSchedules, error: lopErr } = await supabase.from('tbl_lichhoc_lop').select('*').eq('malop', transferClassId);
      if (lopErr) throw lopErr;

      if (lopSchedules && lopSchedules.length > 0) {
        const scheduleDataToInsert = lopSchedules.map(sc => ({
          mahv: selectedStudentId,
          malop: transferClassId,
          lichhoc_lop_id: sc.id,
          lichhoc: sc.lichhoc || '',
          giohoc: sc.giohoc || '',
          loailich: 'class',
          trangthai: 'active'
        }));
        const { error: schedError } = await supabase.from('tbl_lichhoc_hv').insert(scheduleDataToInsert);
        if (schedError) throw schedError;
      } else {
         // Just insert class name if no schedule defined
         const { error: schedError } = await supabase.from('tbl_lichhoc_hv').insert([{
           mahv: selectedStudentId,
           malop: transferClassId,
           loailich: 'class',
           trangthai: 'active'
         }]);
         if (schedError) throw schedError;
      }

      showMessage('success', 'Chuyển lớp thành công!');
      setIsTransferClassOpen(false);
      fetchStudents();
    } catch (err) {
      console.error(err);
      showMessage('error', 'Lỗi khi chuyển lớp: ' + (err.message || ''));
    }
  };

  // Excel Actions
  const handleExportExcel = () => {
    if (students.length === 0) return showMessage('error', 'Không có dữ liệu để xuất');
    const ws = XLSX.utils.json_to_sheet(students);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Học Viên");
    XLSX.writeFile(wb, "danh_sach_hoc_vien.xlsx");
  };

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (data.length > 0) {
          // Process data to match tbl_hv fields exactly as per import columns
          // Simple insert, assuming columns match
          const sanitizedData = data.map(item => {
             const { malop, ...rest } = item;
             return rest;
          });
          const { error } = await supabase.from('tbl_hv').upsert(sanitizedData);
          if (error) throw error;

          showMessage('success', `Đã nhập Excel thành công ${data.length} bản ghi`);
          fetchStudents();
        }
      } catch (err) {
        console.error(err);
        showMessage('error', 'Lỗi xử lý file Excel. Hãy chắc chắn template đúng chuẩn.');
      }
    };
    reader.readAsBinaryString(file);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const renderStudentsTab = () => {
    const dangHoc = students.filter(s => s.trangthai === 'Đang Học').length;
    const baoluu = students.filter(s => s.trangthai === 'Bảo Lưu').length;
    const daNghi = students.filter(s => s.trangthai === 'Đã Nghỉ').length;

    // Auto filter by search
    const filteredStudents = students.filter(s => {
      const matchSearch = (s.tenhv && s.tenhv.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.mahv && s.mahv.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.sdt && s.sdt.includes(searchTerm));
      const matchClass = classFilter ? (s.malop_list && s.malop_list.includes(classFilter)) : true;
      return matchSearch && matchClass;
    });

    // Sort: 'Đã Nghỉ' at the bottom
    const sortedStudents = [...filteredStudents].sort((a, b) => {
      if (a.trangthai === 'Đã Nghỉ' && b.trangthai !== 'Đã Nghỉ') return 1;
      if (a.trangthai !== 'Đã Nghỉ' && b.trangthai === 'Đã Nghỉ') return -1;
      // Secondary sort logic (by mahv)
      return (a.mahv || '').localeCompare(b.mahv || '');
    });

    return (
      <div className="students-tab-content animate-fade-in">
        {message.text && (
          <div className={`message-alert ${message.type}`}>
            {message.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Statistics */}
        <div className="stats-container">
          <div className="stat-card total">
            <div className="stat-icon"><Users size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Tổng Học Viên</span>
              <span className="stat-value">{students.length}</span>
            </div>
          </div>
          <div className="stat-card active">
            <div className="stat-icon"><BookOpen size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Đang Học</span>
              <span className="stat-value">{dangHoc}</span>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon"><RefreshCw size={24} color="#f59e0b" /></div>
            <div className="stat-info">
              <span className="stat-label">Bảo Lưu</span>
              <span className="stat-value">{baoluu}</span>
            </div>
          </div>
          <div className="stat-card inactive">
            <div className="stat-icon"><Users size={24} color="#ef4444" /></div>
            <div className="stat-info">
              <span className="stat-label">Đã Nghỉ</span>
              <span className="stat-value">{daNghi}</span>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-mobile">
            <div className="search-stack">
              <div className="search-item">
                <Search size={16} className="item-icon" />
                <input
                  type="text"
                  placeholder="Lọc tên, SĐT..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="search-item">
                <BookOpen size={16} className="item-icon" />
                <select
                  value={classFilter}
                  onChange={e => setClassFilter(e.target.value)}
                  className="mobile-select"
                >
                  <option value="">Lớp: Tất cả</option>
                  {classes.map(c => <option key={c.malop} value={c.malop}>{c.tenlop}</option>)}
                </select>
              </div>
            </div>

            <div className="action-grid-v2">
              <button className="grid-btn primary" onClick={handleOpenAdd}><UserPlus size={16} /><small>Thêm</small></button>
              <button className="grid-btn info" onClick={handleOpenEdit}><Edit size={16} /><small>Sửa</small></button>
              <button className="grid-btn danger" onClick={handleDeleteTrigger}><Trash2 size={16} /><small>Xóa</small></button>
              <button className="grid-btn warning" onClick={handleReserve}><RefreshCw size={16} /><small>Bảo lưu</small></button>
              <button className="grid-btn transfer" onClick={handleTransferClassTrigger}><ArrowRightLeft size={16} /><small>Chuyển</small></button>
              <button className="grid-btn" style={{color: '#8b5cf6', borderColor: '#8b5cf6'}} onClick={() => {
                if(!selectedStudentId) return showMessage('error', 'Chọn học viên để xếp lịch');
                setIsScheduleOpen(true);
              }}><Calendar size={16} /><small>Xếp Lịch</small></button>
              <button className="grid-btn success" onClick={() => fileInputRef.current?.click()}><FileSpreadsheet size={16} /><small>Nhập</small></button>
              <button className="grid-btn success" onClick={handleExportExcel}><Download size={16} /><small>Xuất</small></button>
              
              <input type="file" accept=".xlsx, .xls" style={{ display: 'none' }} ref={fileInputRef} onChange={handleImportExcel} />
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spinner" size={24} />
              <span>Đang tải dữ liệu học viên...</span>
            </div>
          ) : (
            <>
              <div className="table-scroll-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Mã HV</th>
                      <th>Tên Học Viên</th>
                      <th>Điện Thoại</th>
                      <th>SĐT Phụ Huynh</th>
                      <th>Lớp Đang Học</th>
                      <th>Trạng Thái</th>
                      <th>Ngày Nhập Học</th>
                      <th>Ngày Nghỉ Học</th>
                      <th>Tên Phụ Huynh</th>
                      <th>Trường Đang Học</th>
                      <th>Lớp Ở Trường</th>
                      <th>Địa Chỉ</th>
                      <th>Ghi Chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.length > 0 ? (
                      sortedStudents.map((s) => (
                        <tr
                          key={s.mahv}
                          className={`
                            ${selectedStudentId === s.mahv ? 'selected-row' : ''}
                            ${s.trangthai === 'Đã Nghỉ' ? 'row-inactive' : ''}
                          `}
                          onClick={() => setSelectedStudentId(s.mahv)}
                        >
                          <td className="font-medium">{s.mahv || '-'}</td>
                          <td className="font-semibold text-primary">{s.tenhv || '-'}</td>
                          <td>{s.sdt || '-'}</td>
                          <td>{s.sdtphuhuynh || '-'}</td>
                          <td title={s.malop_list?.join(', ')}>
                            {s.malop_list && s.malop_list.length > 0 ? (
                              s.malop_list.map(ml => classes.find(c => c.malop === ml)?.tenlop || ml).join(', ')
                            ) : '-'}
                          </td>
                          <td>
                            <span className={`status-badge ${s.trangthai === 'Đang Học' ? 'active' :
                              (s.trangthai === 'Bảo Lưu' ? 'warning' : 'inactive')
                              }`}>
                              {s.trangthai || 'Chưa phân loại'}
                            </span>
                          </td>
                          <td>{s.ngaynhaphoc ? new Date(s.ngaynhaphoc).toLocaleDateString('vi-VN') : '-'}</td>
                          <td>{s.ngaynghihoc || '-'}</td>
                          <td>{s.tenphuhuynh || '-'}</td>
                          <td>{s.truongdanghoc || '-'}</td>
                          <td>{s.lopotruong || '-'}</td>
                          <td className="truncate-cell" title={s.diachi}>{s.diachi || '-'}</td>
                          <td className="truncate-cell" title={s.ghichu}>{s.ghichu || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="13" className="empty-state">
                          <div className="empty-state-content">
                            <Users size={40} />
                            <p>Không tìm thấy học viên nào.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ✅ CARD LIST (mobile) */}
              <div className="student-card-list">
                {sortedStudents.length > 0 ? (
                  sortedStudents.map((s, idx) => (
                    <div
                      key={s.mahv}
                      className={`student-card status-${s.trangthai?.replace(/\s+/g, '-').toLowerCase()} ${selectedStudentId === s.mahv ? 'selected' : ''}`}
                      onClick={() => setSelectedStudentId(s.mahv)}
                    >
                      <div className="card-status-bar"></div>
                      <div className="student-card-header">
                        <div>
                          <div className="student-name">{s.tenhv}</div>
                          <div className="student-id">#{s.mahv}</div>
                        </div>
                        <span className={`status-badge ${s.trangthai === 'Đang Học' ? 'active' :
                          (s.trangthai === 'Bảo Lưu' ? 'warning' : 'inactive')
                          }`}>
                          {s.trangthai || 'Chưa cập nhật'}
                        </span>
                      </div>

                      <div className="student-card-body">
                        <div className="simple-info-grid">
                          <div className="info-pair">
                            <span className="label">Lớp:</span>
                            <span className="value">
                              {s.malop_list && s.malop_list.length > 0 ? (
                                s.malop_list.map(ml => classes.find(c => c.malop === ml)?.tenlop || ml).join(', ')
                              ) : '-'}
                            </span>
                          </div>
                          <div className="info-pair">
                            <span className="label">SĐT:</span>
                            <span className="value highlight">{s.sdt || '-'}</span>
                          </div>
                          <div className="info-pair">
                            <span className="label">PH:</span>
                            <span className="value">{s.tenphuhuynh || '-'}</span>
                          </div>
                          <div className="info-pair">
                            <span className="label">SĐT PH:</span>
                            <span className="value">{s.sdtphuhuynh || '-'}</span>
                          </div>
                          <div className="info-pair wide">
                            <span className="label">Trường:</span>
                            <span className="value">{s.truongdanghoc || '-'}{s.lopotruong ? ` (${s.lopotruong})` : ''}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">Không tìm thấy học viên nào.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="student-manager">
      <div className="subtab-content">
        {activeSubTab === 'students' && renderStudentsTab()}
        {activeSubTab === 'classes' && <ClassManager students={students} showMessage={showMessage} fetchStudents={fetchStudents} />}
        {activeSubTab === 'attendance_today' && <AttendanceToday students={students} classes={classes} />}
        {activeSubTab === 'attendance' && <AttendanceManager students={students} showMessage={showMessage} />}
        {activeSubTab === 'leave_list' && <LeaveManager students={students} />}
      </div>

      {/* Form Modal */}
      {isFormOpen && createPortal(
        <div className="modal-overlay">
          <div className="modal-content form-modal">
            <div className="modal-header">
              <h3>{isEditMode ? 'Sửa thông tin Học Viên' : 'Thêm Học Viên Mới'}</h3>
              <button className="close-btn" onClick={() => setIsFormOpen(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="modal-body form-grid">
              <div className="form-group">
                <label>Mã HV (Tự động)</label>
                <input type="text" name="mahv" value={formData.mahv} onChange={handleChange} required disabled={true} />
              </div>
              <div className="form-group">
                <label>Tên HV *</label>
                <input type="text" name="tenhv" value={formData.tenhv} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Số Điện Thoại</label>
                <input type="text" name="sdt" value={formData.sdt} onChange={handleChange} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Lớp Học (Có thể chọn nhiều lớp)</label>
                {selectedClasses.map((cls, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select
                      value={cls}
                      onChange={(e) => {
                        const newClasses = [...selectedClasses];
                        newClasses[index] = e.target.value;
                        setSelectedClasses(newClasses);
                      }}
                      className="form-control"
                    >
                      <option value="">-- Chọn lớp --</option>
                      {classes.map(c => <option key={c.malop} value={c.malop}>{c.tenlop}</option>)}
                    </select>
                    {selectedClasses.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => {
                          const newClasses = selectedClasses.filter((_, i) => i !== index);
                          setSelectedClasses(newClasses);
                        }}
                        style={{ padding: '0 10px', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', background: 'transparent', cursor: 'pointer' }}
                        title="Xóa lớp"
                      >
                         <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => setSelectedClasses([...selectedClasses, ''])}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#8b5cf6', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: '0.9rem', fontWeight: '500' }}
                >
                  <Plus size={16} /> Thêm lớp
                </button>
              </div>
              <div className="form-group">
                <label>Trạng Thái</label>
                <select name="trangthai" value={formData.trangthai} onChange={handleChange}>
                  <option value="Đang Học">Đang Học</option>
                  <option value="Bảo Lưu">Bảo Lưu</option>
                  <option value="Đã Nghỉ">Đã Nghỉ</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ngày Nhập Học</label>
                <input type="date" name="ngaynhaphoc" value={formData.ngaynhaphoc} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>SĐT Phụ Huynh</label>
                <input type="text" name="sdtphuhuynh" value={formData.sdtphuhuynh} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Tên Phụ Huynh</label>
                <input type="text" name="tenphuhuynh" value={formData.tenphuhuynh} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Trường Đang Học</label>
                <input type="text" name="truongdanghoc" value={formData.truongdanghoc} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Lớp Ở Trường</label>
                <input type="text" name="lopotruong" value={formData.lopotruong} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Địa Chỉ</label>
                <input type="text" name="diachi" value={formData.diachi} onChange={handleChange} />
              </div>
              <div className="form-group full-width">
                <label>Ghi Chú</label>
                <textarea name="ghichu" value={formData.ghichu} onChange={handleChange} rows="2"></textarea>
              </div>
              <div className="form-actions full-width">
                <button type="button" className="btn btn-outline" onClick={() => setIsFormOpen(false)}>Hủy</button>
                <button type="submit" className="btn btn-primary">{isEditMode ? 'Cập Nhật' : 'Thêm Mới'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Transfer Class Modal */}
      {isTransferClassOpen && createPortal(
        <div className="modal-overlay">
          <div className="modal-content sm-modal">
            <div className="modal-header">
              <h3><ArrowRightLeft size={20} /> Chuyển Học Viên Sang Lớp Khác</h3>
              <button className="close-btn" onClick={() => setIsTransferClassOpen(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p>Học viên đang chọn: <strong>{students.find(s => s.mahv === selectedStudentId)?.tenhv || selectedStudentId}</strong></p>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Chọn lớp mới:</label>
                <select
                  value={transferClassId}
                  onChange={(e) => setTransferClassId(e.target.value)}
                  style={{ width: '100%', height: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="">-- Click để chọn lớp --</option>
                  {classes.map(c => (
                    <option key={c.malop} value={c.malop}>{c.tenlop} ({c.malop})</option>
                  ))}
                </select>
              </div>
              <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                <button className="btn btn-outline" onClick={() => setIsTransferClassOpen(false)}>Hủy bỏ</button>
                <button className="btn btn-primary" style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }} onClick={confirmTransferClass}>Xác nhận chuyển</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteOpen && createPortal(
        <div className="modal-overlay">
          <div className="modal-content sm-modal">
            <div className="modal-header delete-header">
              <h3><AlertCircle size={20} /> Xác nhận xóa học viên</h3>
              <button className="close-btn" onClick={() => setIsDeleteOpen(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p>Bạn có chắc chắn muốn xóa học viên <strong>{selectedStudentId}{students.find(s => s.mahv === selectedStudentId)?.tenhv ? ` - ${students.find(s => s.mahv === selectedStudentId).tenhv}` : ''}</strong>? Thao tác này không thể hoàn tác.</p>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Vui lòng nhập mật khẩu của bạn để xác nhận:</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Mật khẩu của bạn..."
                />
              </div>
              <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                <button className="btn btn-outline" onClick={() => setIsDeleteOpen(false)}>Hủy bỏ</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Xác nhận xóa</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isScheduleOpen && selectedStudentId && (
        <StudentScheduleModal 
          student={students.find(s => s.mahv === selectedStudentId)}
          classes={classes}
          onClose={() => setIsScheduleOpen(false)}
        />
      )}
      
      {/* Mobile Selection Action Bar */}
      {selectedStudentId && (
        <div className="mobile-action-bar animate-slide-up">
          <button className="action-btn warning" onClick={handleReserve} title="Bảo lưu / Hủy">
            <RefreshCw size={20} />
            <span>Bảo lưu</span>
          </button>
          <button className="action-btn primary-purple" onClick={handleTransferClassTrigger}>
            <ArrowRightLeft size={20} />
            <span>Chuyển lớp</span>
          </button>
          <button className="action-btn" style={{color: '#8b5cf6'}} onClick={() => setIsScheduleOpen(true)}>
            <Calendar size={20} />
            <span>Xếp Lịch</span>
          </button>
          <button className="action-btn danger" onClick={handleDeleteTrigger}>
            <Trash2 size={20} />
            <span>Xóa HV</span>
          </button>
          <button className="action-btn secondary" onClick={() => setSelectedStudentId(null)}>
            <X size={20} />
            <span>Đóng</span>
          </button>
        </div>
      )}
    </div>
  );
}
